import type { SelectOption } from '@fruuit/ui'

/** Options for a `<Select>` from a shared vocabulary (values plus display labels). */
export function vocabOptions<T extends string>(vocab: {
  values: readonly T[]
  labels: Readonly<Record<T, string>>
}): SelectOption[] {
  return vocab.values.map((value) => ({ value, label: vocab.labels[value] }))
}

/** Label for a vocabulary value, falling back to the raw value. */
export function vocabLabel<T extends string>(
  vocab: { labels: Readonly<Record<T, string>> },
  value: string | null | undefined,
): string {
  if (!value) return '—'
  return (vocab.labels as Record<string, string>)[value] ?? value
}

const DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
})

/** "22 Oct 2026" for a `YYYY-MM-DD` date or an ISO instant; "—" when missing. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const iso = value.length === 10 ? `${value}T00:00:00Z` : value
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? value : DATE.format(date)
}

/** "22 Oct 2026, 09:00 UTC". */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : `${DATE_TIME.format(date)} UTC`
}

/** Today's date as `YYYY-MM-DD` (UTC). */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Shorten a hash for tables: first 12 characters plus an ellipsis. */
export function shortHash(hash: string, length = 12): string {
  return hash.length > length ? `${hash.slice(0, length)}…` : hash
}
